// ARCA-587 — META-08 emote picker, refactored from an always-on 10-button
// strip into a compact on-demand trigger + sub-menu.
//
// A single react/smiley trigger button docks in the opponent-turn control
// region (next to the board-view inspect control). Tapping it opens the
// 10-emote grid inside the shared <Sheet> primitive (take-two re-skin WS2);
// selecting an emote calls onSend(id) and closes the menu.
//
// Why a trigger, not a strip: emotes are an occasional, rate-limited (1/10s)
// reaction usable only on the opponent's turn — an always-on 10-wide toolbar
// over-weights that. The old faded "disabled on your turn" affordance is gone:
// MatchScreen only mounts this in the opponent-turn region, so the picker
// simply isn't present on your own turn (no disabled state to render).
//
// Preserved: the 10 emote ids (mirror Plan 20-03 backend EMOTE_LIST 1:1), the
// per-emote SVG icons (/emotes/<id>.svg), i18n aria-labels (emotes.<id>.name),
// role=toolbar + keyboard nav inside the menu, and the sendEmote WS path +
// client/server rate-limits (all unchanged — this is a presentation refactor).
//
// The 10 emote ids mirror the backend EMOTE_LIST 1:1. Adding an emote requires
// extending the backend list, regenerating the proto, AND adding the matching
// i18n + SVG asset.

import { type JSX, useId, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Icon } from '../../components/Icon'
import { Sheet } from '../../components/Sheet'

/** 10 emote ids — mirrors Plan 20-03 backend EMOTE_LIST + Plan 20-06 i18n keys. */
export const EMOTE_IDS = [
  'wow',
  'gg',
  'oof',
  'mind_blown',
  'thinking',
  'clap',
  'fire',
  'hundred',
  'pray',
  'salt',
] as const

export type EmoteId = (typeof EMOTE_IDS)[number]

export interface EmotePickerProps {
  /** Called with the emote id when an emote is selected from the menu. */
  readonly onSend: (emoteId: EmoteId) => void
}

export function EmotePicker({ onSend }: EmotePickerProps): JSX.Element {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const titleId = useId()

  const handleSelect = (id: EmoteId): void => {
    onSend(id)
    setOpen(false)
  }

  return (
    <>
      <button
        type="button"
        className="emote-trigger"
        data-screen-region="emote-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={intl.formatMessage({ id: 'match.emote_picker_aria_label' })}
        onClick={() => {
          setOpen(true)
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          padding: 0,
          background: 'var(--color-surface-mid)',
          border: '2px solid var(--color-surface-stroke)',
          borderRadius: 'var(--radius-pill)',
          color: 'var(--color-text-primary)',
          cursor: 'pointer',
        }}
      >
        <Icon name="smiley" size={24} />
      </button>

      {open ? (
        // Bottom-sheet flavor (UI-SPEC §5): scrim + grab handle + focus trap +
        // scrim-tap/Escape dismissal all live in <Sheet>/ModalScrim.
        <Sheet
          onClose={() => {
            setOpen(false)
          }}
          testId="emote-picker-sheet"
          labelledBy={titleId}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              padding: 'var(--space-2) var(--space-3) var(--space-3)',
            }}
          >
            <h2
              id={titleId}
              style={{
                margin: 0,
                textAlign: 'center',
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-18)',
                color: 'var(--color-text-primary)',
              }}
            >
              <FormattedMessage
                id="match.emote_menu_title"
                defaultMessage="Send an emote"
              />
            </h2>
            <div
              className="emote-picker"
              role="toolbar"
              data-screen-region="emote-picker"
              aria-labelledby={titleId}
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 1fr)',
                gap: 'var(--space-2)',
                justifyItems: 'center',
              }}
            >
              {EMOTE_IDS.map((id) => (
                <button
                  key={id}
                  type="button"
                  className="emote-button"
                  data-emote-id={id}
                  aria-label={intl.formatMessage({ id: `emotes.${id}.name` })}
                  onClick={() => {
                    handleSelect(id)
                  }}
                  style={{
                    width: 52,
                    height: 52,
                    padding: 4,
                    background: 'var(--color-surface-mid)',
                    border: '2px solid var(--color-surface-stroke)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                  }}
                >
                  <img
                    src={`/emotes/${id}.svg`}
                    alt=""
                    aria-hidden="true"
                    width={36}
                    height={36}
                    style={{ display: 'block', margin: '0 auto' }}
                  />
                </button>
              ))}
            </div>
          </div>
        </Sheet>
      ) : null}
    </>
  )
}
